import { Schema } from 'effect';
import {
  HttpApiEndpoint,
  HttpApiGroup,
  HttpApiSchema,
} from 'effect/unstable/httpapi';
import {
  BadRequestError,
  InternalServerError,
  integrationProviderErrors,
} from './errors';
import { OptionalUserAuthMiddleware, UserAuthMiddleware } from './middleware';
import {
  DOCUMENT_TYPE_BY_MIME,
  uploadableDocumentTypeFromMime,
} from './schemas/document-type';

export const GOOGLE_DRIVE_SCOPE = 'https://www.googleapis.com/auth/drive.file';
export const GOOGLE_DRIVE_CALLBACK_PATH =
  '/api/v2/integrations/google-drive/callback';
export const googleDriveMobileCallbackUrl = (scheme: string) =>
  `${scheme}://google-drive-callback`;
export const GoogleDriveMobileCompletion = Schema.Struct({
  state: Schema.String,
  code: Schema.String,
  file_ids: Schema.Array(Schema.String).check(Schema.isMaxLength(100)),
});
export const GoogleDriveSelectedFiles = Schema.Struct({
  files: Schema.Array(
    Schema.Struct({ file_id: Schema.String, name: Schema.String }),
  ),
});
export const GOOGLE_DRIVE_EXPORT_TYPES = [
  'application/vnd.google-apps.document',
  'application/vnd.google-apps.presentation',
] as const;
export const GOOGLE_DRIVE_MIME_TYPES = [
  ...Object.keys(DOCUMENT_TYPE_BY_MIME).filter(
    (mime) => uploadableDocumentTypeFromMime(mime) !== undefined,
  ),
  ...GOOGLE_DRIVE_EXPORT_TYPES,
];

export const GoogleDriveFileSelection = Schema.Struct({
  file_id: Schema.String,
  resource_key: Schema.optional(Schema.String),
});

export const GoogleDriveImportSourceSchema = Schema.Struct({
  ...GoogleDriveFileSelection.fields,
  connection_id: Schema.String,
  mime_type: Schema.String,
});
export type GoogleDriveImportSource = typeof GoogleDriveImportSourceSchema.Type;

export const GoogleDriveStatus = Schema.Struct({
  available: Schema.Boolean,
  connected: Schema.Boolean,
  display_name: Schema.NullOr(Schema.String),
});

export const googleDriveGroup = HttpApiGroup.make('GoogleDrive')
  .add(
    HttpApiEndpoint.post('StartGoogleDriveMobile', '/mobile/start', {
      payload: Schema.Struct({ pick_files: Schema.Boolean }),
      success: Schema.Struct({ url: Schema.String, state: Schema.String }),
      error: integrationProviderErrors,
    }).middleware(UserAuthMiddleware),
  )
  .add(
    HttpApiEndpoint.post('CompleteGoogleDriveMobile', '/mobile/complete', {
      payload: GoogleDriveMobileCompletion,
      success: GoogleDriveSelectedFiles,
      error: integrationProviderErrors,
    }).middleware(UserAuthMiddleware),
  )
  .add(
    HttpApiEndpoint.get('GetGoogleDriveStatus', '/status', {
      success: GoogleDriveStatus,
      error: InternalServerError,
    }).middleware(UserAuthMiddleware),
  )
  .add(
    HttpApiEndpoint.get('ConnectGoogleDrive', '/connect', {
      success: HttpApiSchema.Empty(302),
      error: [BadRequestError, InternalServerError],
    }).middleware(OptionalUserAuthMiddleware),
  )
  .add(
    HttpApiEndpoint.get('GoogleDriveCallback', '/callback', {
      query: Schema.Struct({
        code: Schema.optional(Schema.String),
        state: Schema.optional(Schema.String),
        error: Schema.optional(Schema.String),
        picked_file_ids: Schema.optional(Schema.String),
      }),
      success: HttpApiSchema.Empty(302),
      error: InternalServerError,
    }).middleware(OptionalUserAuthMiddleware),
  )
  .add(
    HttpApiEndpoint.post('GetGoogleDrivePicker', '/picker', {
      success: Schema.Struct({
        access_token: Schema.String,
        api_key: Schema.String,
        app_id: Schema.String,
      }),
      error: integrationProviderErrors,
    }).middleware(UserAuthMiddleware),
  )
  .add(
    HttpApiEndpoint.delete('DisconnectGoogleDrive', '/disconnect', {
      success: Schema.Void.pipe(HttpApiSchema.status(204)),
      error: integrationProviderErrors,
    }).middleware(UserAuthMiddleware),
  )
  .prefix('/v2/integrations/google-drive');
